import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";

import {
  fetchInsideSalesAddPortalUserContext,
  previewInsideSalesPendingPortalUserOrganization,
  searchInsideSalesWirelessPortalOrganizations,
} from "./insideSalesWirelessPortalAccountsApi";
import { DEFAULT_US_TIME_ZONE, US_TIME_ZONE_OPTIONS } from "./insideSalesWirelessPortalTimeZones";

const ROLE_OPTIONS = [
  { value: "2", label: "Organization admin" },
  { value: "3", label: "Group admin" },
  { value: "4", label: "User" },
];

export function AddWirelessPortalUserModal(props: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onCreate: (input: {
    organizationId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timeZone: string;
    accessLevel: number;
  }) => void;
}) {
  const { open, pending, onClose, onCreate } = props;
  const [organizationId, setOrganizationId] = useState("");
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [timeZone, setTimeZone] = useState(DEFAULT_US_TIME_ZONE);
  const [timeZoneSearch, setTimeZoneSearch] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [accessLevel, setAccessLevel] = useState("4");

  useEffect(() => {
    if (!open) return;
    setOrganizationId("");
    setOrganizationSearch("");
    setTimeZone(DEFAULT_US_TIME_ZONE);
    setTimeZoneSearch("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAccessLevel("4");
  }, [open]);

  const organizationsQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-organizations", "all"],
    queryFn: () => searchInsideSalesWirelessPortalOrganizations(""),
    enabled: open,
  });
  const organizations = organizationsQuery.data?.organizations ?? [];
  const selected = useMemo(
    () => organizations.find((organization) => String(organization.organizationId) === organizationId) ?? null,
    [organizationId, organizations],
  );

  const contextQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-add-user-context", organizationId],
    queryFn: () => fetchInsideSalesAddPortalUserContext(Number(organizationId)),
    enabled: open && organizationId !== "",
  });

  useEffect(() => {
    if (contextQuery.data == null) return;
    setTimeZone(contextQuery.data.timeZone || DEFAULT_US_TIME_ZONE);
    if (!contextQuery.data.hasPortalAdmin) setAccessLevel("2");
  }, [contextQuery.data]);

  const emails = email.trim() === "" ? [] : [email.trim().toLowerCase()];
  const membershipQuery = useQuery({
    queryKey: ["inside-sales", "wireless-portal-add-user-review", organizationId, emails.join(",")],
    queryFn: () =>
      previewInsideSalesPendingPortalUserOrganization(
        emails,
        organizationId === "" ? undefined : Number(organizationId),
      ),
    enabled: open && emails.length > 0,
  });

  if (!open) return null;

  const roleLocked = organizationId !== "" && contextQuery.data?.hasPortalAdmin === false;
  const person = membershipQuery.data?.people[0];
  const errors = person?.errors ?? [];
  const warnings = person?.warnings ?? [];
  const canSubmit =
    selected != null &&
    timeZone !== "" &&
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    email.trim() !== "" &&
    membershipQuery.data?.ok === true &&
    !membershipQuery.isFetching &&
    !contextQuery.isFetching &&
    !pending;

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("form")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-portal-user-title"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 px-4 py-3">
          <h2 id="add-portal-user-title" className="text-base font-semibold text-slate-900">
            Add portal user
          </h2>
          <HoverHintWithGlyph hoverHint="Creates a user in an existing portal organization and emails a password setup link. If the organization has no org admin yet, the role is locked to organization admin." />
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            if (selected == null || !canSubmit) return;
            onCreate({
              organizationId: selected.organizationId,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim(),
              phone: phone.trim(),
              timeZone,
              accessLevel: Number(accessLevel),
            });
          }}
        >
          <div className={`${hubModalBodyScrollClassName()} space-y-3 px-4 py-4`}>
            <div className="space-y-1">
              <Label htmlFor="add-portal-user-org">Portal organization</Label>
              <SearchableSelect
                id="add-portal-user-org"
                value={organizationId}
                onValueChange={(option) => setOrganizationId(option?.value ?? "")}
                options={organizations.map((organization) => ({
                  value: String(organization.organizationId),
                  label: organization.name,
                  description: organization.domain,
                }))}
                searchValue={organizationSearch}
                onSearchChange={setOrganizationSearch}
                placeholder="Search and select a portal organization"
                searchPlaceholder="Search by name or domain"
                emptyMessage={
                  organizationsQuery.isError
                    ? organizationsQuery.error instanceof ApiError
                      ? organizationsQuery.error.message
                      : "Could not load portal organizations."
                    : organizationsQuery.isLoading
                      ? "Loading portal organizations..."
                      : "No portal organizations found."
                }
                isLoading={organizationsQuery.isLoading}
                className="mt-1"
              />
              {contextQuery.isError ? (
                <p className="text-xs text-rose-700">
                  {contextQuery.error instanceof ApiError
                    ? contextQuery.error.message
                    : "Could not load this organization."}
                </p>
              ) : null}
              {roleLocked ? (
                <p className="text-xs text-amber-800">
                  This organization has no org admin. Role is locked to organization admin.
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-portal-user-timezone">Time zone</Label>
              <SearchableSelect
                id="add-portal-user-timezone"
                value={timeZone}
                onValueChange={(option) => setTimeZone(option?.value ?? "")}
                options={US_TIME_ZONE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                  description: option.value,
                }))}
                searchValue={timeZoneSearch}
                onSearchChange={setTimeZoneSearch}
                placeholder="Search a US time zone"
                searchPlaceholder="Search Eastern, Central, Pacific…"
                emptyMessage="No matching time zones."
                className="mt-1"
              />
              {contextQuery.data?.timeZoneHint ? (
                <p className="text-xs text-slate-500">{contextQuery.data.timeZoneHint}</p>
              ) : null}
            </div>

            <label className="block space-y-1 text-sm text-slate-700">
              <span>First name</span>
              <Input value={firstName} autoComplete="off" onChange={(event) => setFirstName(event.target.value)} />
            </label>
            <label className="block space-y-1 text-sm text-slate-700">
              <span>Last name</span>
              <Input value={lastName} autoComplete="off" onChange={(event) => setLastName(event.target.value)} />
            </label>
            <label className="block space-y-1 text-sm text-slate-700">
              <span>Email</span>
              <Input
                type="email"
                value={email}
                autoComplete="off"
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="block space-y-1 text-sm text-slate-700">
              <span>Phone (optional)</span>
              <Input value={phone} autoComplete="off" onChange={(event) => setPhone(event.target.value)} />
            </label>

            <label className="block space-y-1 text-sm text-slate-700">
              <span>Role</span>
              <select
                id="add-portal-user-role"
                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
                value={accessLevel}
                disabled={roleLocked || pending}
                onChange={(event) => setAccessLevel(event.target.value)}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {membershipQuery.isFetching ? (
              <p className="text-xs text-slate-500">Checking email against the portal...</p>
            ) : null}
            {membershipQuery.isError ? (
              <p className="text-xs text-rose-700">
                {membershipQuery.error instanceof ApiError
                  ? membershipQuery.error.message
                  : "Could not check this email."}
              </p>
            ) : null}
            {errors.map((message) => (
              <p key={message} className="text-xs text-rose-700">
                {message}
              </p>
            ))}
            {warnings.map((message) => (
              <p key={message} className="text-xs text-amber-800">
                {message}
              </p>
            ))}
          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3">
            <Button type="button" variant="secondary" disabled={pending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} isLoading={pending}>
              Add user
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
